<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';

import AmcLogo from '../../svg/amc-logo-horizontal.svg';
import AuthFlow from '../shared/auth/components/AuthFlow.vue';
import type { AuthenticationDestination, AuthenticationStart } from '../shared/auth/types.ts';
import PublicPageLayout from '../shared/components/PublicPageLayout.vue';
import { usePublicFlowAdapter } from '../shared/publicFlows/adapter.ts';
import type { RegistrationProduct } from './router.ts';

const props = defineProps<{
    start: AuthenticationStart;
    intended?: string;
    product?: RegistrationProduct;
}>();

const router = useRouter();
const adapter = usePublicFlowAdapter();
const registration = computed(
    () => props.start.screen === 'entry' && props.start.mode === 'register',
);
const amcProduct = computed(() => registration.value && props.product?.slug.startsWith('amc'));
const promotedProduct = computed(() =>
    registration.value && props.product && !amcProduct.value ? props.product : undefined,
);
const trustMessage =
    'Thousands of medical students and doctors trust eMedici to take them from student to specialist.';

function navigate(destination: AuthenticationDestination, transition: 'next' | 'prev' = 'next') {
    if (destination === 'password-recovery') {
        void router.push({ name: 'auth.password-recovery', state: { transition: 'next' } });
        return;
    }

    void router.replace({
        name: 'auth.entry',
        params: { mode: destination },
        state: { transition },
    });
}

function complete(redirect?: string) {
    window.location.assign(redirect || props.intended || '/');
}
</script>

<template>
    <PublicPageLayout
        :brand-url="adapter.brandUrl"
        :product="promotedProduct"
        :hide-intro-on-mobile="!registration"
        :description="!registration || !product ? trustMessage : undefined"
        :title="registration ? 'Start Your Free Trial' : undefined"
    >
        <template v-if="!registration" #title>
            <span>
                Smarter Study,
                <br />
                Better Doctors.
            </span>
        </template>

        <template v-if="amcProduct" #branding>
            <AmcLogo class="align-self-center" style="width: 80%" />
        </template>

        <AuthFlow
            autofocus
            :intended="intended"
            :start="start"
            @complete="complete"
            @navigate="navigate"
        />
    </PublicPageLayout>
</template>
